import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { VehiclePicker, type VehicleSelection } from '@/features/quotes/pricing/VehiclePicker';
import { useVehicle } from '@/features/customers/vehiclesApi';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCreatePrintDesign } from './api';
import { parseVehicleLabel } from './helpers';

export function NewPrintDesignPage() {
  const { t } = useTranslation('printWraps');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const createDesign = useCreatePrintDesign(workspaceId);

  const yearParam = searchParams.get('year');
  const makeParam = searchParams.get('make') ?? '';
  const modelParam = searchParams.get('model') ?? '';
  const colorParam = searchParams.get('color') ?? '';
  const customerVehicleId = searchParams.get('customerVehicleId');
  const returnTo = searchParams.get('returnTo');

  const { data: customerVehicle } = useVehicle(workspaceId, customerVehicleId ?? undefined);

  const [name, setName] = useState('');
  const [selection, setSelection] = useState<VehicleSelection | null>(null);
  const [color, setColor] = useState(colorParam);

  useEffect(() => {
    if (!colorParam && color === '' && customerVehicle?.color) setColor(customerVehicle.color);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerVehicle?.color]);

  const customerVehicleHint = makeParam || modelParam || yearParam
    ? { year: yearParam ? Number(yearParam) : null, make: makeParam, model: modelParam }
    : null;

  const canSubmit = name.trim().length > 0 && !!selection;

  async function handleCreate() {
    if (!selection || !name.trim()) return;
    const { year, make, model } = parseVehicleLabel(selection.label);
    try {
      const design = await createDesign.mutateAsync({
        name: name.trim(),
        vehicleYear: year,
        vehicleMake: make || makeParam || '',
        vehicleModel: model || modelParam || '',
        vehicleLabel: selection.label,
        sizeClass: selection.sizeClass,
        catalogVehicleId: selection.catalogVehicleId,
        catalogTrimId: selection.catalogTrimId,
        workspaceVehicleId: selection.workspaceVehicleId,
        vehicleColor: color.trim(),
        // No UI to set this any more (the customer-photo path is removed) -
        // the field itself stays on the schema for the backend/other callers.
        vehiclePhotoMediaId: null,
      });
      const dest = returnTo ? `/print-wraps/${design.id}?returnTo=${encodeURIComponent(returnTo)}` : `/print-wraps/${design.id}`;
      navigate(dest);
    } catch {
      toast.error(t('newDesign.toast.createFailed'));
    }
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <div>
        <h1 className="text-lg font-bold text-ink">{t('newDesign.title')}</h1>
        <p className="text-sm text-muted">{t('newDesign.subtitle')}</p>
      </div>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>{t('newDesign.detailsTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="print-design-name">{t('newDesign.nameLabel')}</Label>
            <Input id="print-design-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('newDesign.namePlaceholder')} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{t('newDesign.vehicleLabel')}</Label>
            <VehiclePicker workspaceId={workspaceId} selection={selection} onSelect={setSelection} customerVehicleHint={customerVehicleHint} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="print-design-color">{t('newDesign.colorLabel')}</Label>
            <Input
              id="print-design-color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              placeholder={t('newDesign.colorPlaceholder')}
            />
            <p className="text-xs text-muted">{t('newDesign.colorHint')}</p>
          </div>
          <Button loading={createDesign.isPending} disabled={!canSubmit} onClick={handleCreate}>
            {t('newDesign.createButton')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
